import { useTranslation } from 'react-i18next';
import { BarChart3, Trophy, Users, TrendingUp } from 'lucide-react';
import { getScoreColor } from '../helpers';
import MiniKpiBar from '../shared/MiniKpiBar';

export default function MockupTeamDashboard() {
  const { t } = useTranslation();

  const stats = [
    { icon: BarChart3, label: t('landing.mockupSimulations'), value: '48', sub: t('landing.mockupPracticeHours'), color: 'text-foreground', iconColor: 'text-primary' },
    { icon: Trophy, label: t('landing.mockupAvgScore'), value: '74.2', sub: t('landing.mockupLastSessions'), color: 'text-yellow-500', iconColor: 'text-yellow-500' },
    { icon: Users, label: t('landing.mockupMostActive'), value: 'María G.', sub: t('landing.mockupMostActiveSims'), color: 'text-foreground', iconColor: 'text-primary' },
    { icon: TrendingUp, label: t('landing.mockupTeamImprovement'), value: '+12.4%', sub: t('landing.mockupVsPreviousMonth'), color: 'text-green-500', iconColor: 'text-green-500' },
  ];

  const vendors = [
    {
      name: 'MARÍA G.', initials: 'MG', score: 91, color: 'var(--primary)',
      tier: 'elite' as const, badge: 'Elite',
      kpis: [
        { name: 'APE', score: 88 }, { name: 'ESC', score: 85 },
        { name: 'PRO', score: 94 }, { name: 'OBJ', score: 90 },
        { name: 'CIE', score: 92 }, { name: 'RAP', score: 87 },
      ],
      sims: 18, last: '27 feb',
    },
    {
      name: 'CARLOS R.', initials: 'CR', score: 78, color: 'var(--primary)',
      tier: 'pro' as const, badge: 'Pro',
      kpis: [
        { name: 'APE', score: 80 }, { name: 'ESC', score: 75 },
        { name: 'PRO', score: 82 }, { name: 'OBJ', score: 70 },
        { name: 'CIE', score: 78 }, { name: 'RAP', score: 76 },
      ],
      sims: 12, last: '26 feb',
    },
    {
      name: 'ANA P.', initials: 'AP', score: 65, color: 'var(--color-blue-500)',
      tier: 'standard' as const, badge: '',
      kpis: [
        { name: 'APE', score: 68 }, { name: 'ESC', score: 60 },
        { name: 'PRO', score: 72 }, { name: 'OBJ', score: 55 },
        { name: 'CIE', score: 66 }, { name: 'RAP', score: 70 },
      ],
      sims: 7, last: '25 feb',
    },
  ];

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="mb-4 grid grid-cols-2 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-3">
            <div className="mb-2 flex h-7 w-7 items-center justify-center rounded-lg bg-muted">
              <s.icon className={`h-3.5 w-3.5 ${s.iconColor}`} />
            </div>
            <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] font-medium text-muted-foreground">{s.label}</p>
            <p className="text-[9px] text-muted-foreground/70">{s.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {vendors.map((v) => {
          const inner = (
            <div
              className={`rounded-2xl bg-card p-3 text-center ${
                v.tier === 'elite' ? '' :
                v.tier === 'pro' ? 'border border-primary/40' : 'border border-border'
              }`}
            >
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xl font-extrabold" style={{ color: getScoreColor(v.score) }}>
                  {v.score}
                </span>
                {v.badge && (
                  <span
                    className="rounded-full px-1.5 py-0.5 text-[8px] font-bold"
                    style={{ backgroundColor: `${v.color}20`, color: v.color }}
                  >
                    {v.badge}
                  </span>
                )}
              </div>

              <div className="mb-1.5 flex justify-center">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: v.color }}
                >
                  {v.initials}
                </div>
              </div>

              <p className="mb-2 text-[9px] font-bold uppercase tracking-wide text-foreground">
                {v.name}
              </p>

              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                {v.kpis.map((k) => (
                  <MiniKpiBar key={k.name} label={k.name} score={k.score} variant="horizontal" />
                ))}
              </div>

              <div className="mt-2 flex items-center justify-between border-t border-border pt-1.5 text-[8px] text-muted-foreground">
                <span>{v.sims} sim.</span>
                <span>{v.last}</span>
              </div>
            </div>
          );

          if (v.tier === 'elite') {
            return (
              <div key={v.name} className="rounded-2xl bg-gradient-to-br from-primary via-[#C9A84C] to-primary p-[1px] card-glow">
                {inner}
              </div>
            );
          }
          return <div key={v.name}>{inner}</div>;
        })}
      </div>
    </div>
  );
}
